// 초대 링크(/join/코드)를 연 사람 — 어느 업체인지 먼저 보여 주고, 「등록」을 눌러야 그 업체에 등록한다(링크가 새도 자동 등록되지 않게)
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { useAuth } from "@/store/auth";

import { plainError } from "@/lib/plainError";
type Res = { ok: boolean; preview?: boolean; role: string; orgName: string };
const accept = httpsCallable<{ code: string; dryRun?: boolean }, Res>(functions, "acceptInvite");
const plain = (e: unknown) => plainError(e, "초대를 확인하지 못했습니다.");

export default function Join() {
  const { code = "" } = useParams();
  const { user, signOut } = useAuth();
  const [preview, setPreview] = useState<Res | null>(null);
  const [msg, setMsg] = useState("초대를 확인하는 중입니다…");
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  // 1단계 — 어느 업체의 초대인지만 확인한다(등록 안 함)
  useEffect(() => {
    if (!user || started.current) return;
    started.current = true;
    accept({ code, dryRun: true })
      .then((r) => { setPreview(r.data); setMsg(""); })
      .catch((e) => { setFailed(true); setMsg(plain(e)); });
  }, [user, code]);

  // 2단계 — 사람이 누른 뒤에만 등록
  const join = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      const r = await accept({ code });
      setPreview(null);
      setMsg(`「${r.data.orgName || "업체"}」에 ${r.data.role === "admin" ? "관리자" : "기사"}로 등록되었습니다. 잠시만요…`);
      try {
        await user.getIdToken(true); // 새 권한을 바로 받아 다시 로그인하지 않아도 되게
      } catch {
        // 등록은 끝났다 — 여기서 붉은 오류를 띄우면 다른 계정으로 또 등록하게 만든다(11-#4)
        setMsg("등록은 끝났습니다. 인터넷이 불안정해 화면을 새로 받지 못했습니다. 앱을 닫고 다시 열어 주세요.");
        setBusy(false);
        return;
      }
      window.location.replace("/");
    } catch (e) {
      setFailed(true);
      setMsg(plain(e));
      setBusy(false);
    }
  };

  const roleText = preview?.role === "admin" ? "관리자" : "기사";
  return (
    <div className="min-h-screen flex items-center justify-center bg-milk-50 px-4">
      <div className="card p-7 w-full max-w-md space-y-4 text-center">
        <h1 className="text-lg font-bold">무상우유콕 초대</h1>
        {preview && (
          <>
            <p className="text-base">
              <b>「{preview.orgName || "업체"}」</b>의 {roleText}로 등록할까요?
            </p>
            <p className="text-sm text-slate-500">
              등록하면 이 업체의 배송 명단을 보고 배송을 기록하게 됩니다. 이 업체 사장님께 받은 링크가 아니면 등록하지 마세요.
            </p>
            <p className="text-sm text-slate-500">로그인 계정: {user?.email}</p>
            <button className="btn-primary w-full" onClick={join} disabled={busy}>{busy ? "등록 중…" : `${roleText}로 등록`}</button>
            <button className="btn-secondary w-full" onClick={() => signOut()} disabled={busy}>아니요, 다른 계정으로 로그인</button>
          </>
        )}
        {msg && <p className={failed ? "text-red-600" : "text-slate-600"} role="status">{msg}</p>}
        {failed && (
          <div className="text-sm text-slate-500 space-y-2">
            <p>로그인 계정: {user?.email}</p>
            <button className="btn-secondary w-full" onClick={() => signOut()}>다른 구글 계정으로 로그인</button>
            <a className="underline block" href="tel:01026085099">도움이 필요하면 010-2608-5099 (평일 9시~18시)</a>
          </div>
        )}
      </div>
    </div>
  );
}
